import * as React from 'react'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Confirm Dialog
 *
 * A pre-built destructive action confirmation dialog.
 * Maroon destructive button pattern, Trajan Pro Bold title.
 *
 * For non-destructive confirmations, use the base Modal component.
 *
 * Features:
 * - Destructive (maroon) and default (navy) variants
 * - Icon slot (warning triangle, trash, etc.)
 * - Trajan Pro Bold title, Garamond description
 * - Focus on cancel button (safe default)
 * - Enter to confirm, Escape to cancel
 * - Optional exact-text confirmation for high-risk actions
 * - Accessible: role="alertdialog"
 *
 * Usage:
 *   <ConfirmDialog
 *     open={isConfirmOpen}
 *     onOpenChange={setIsConfirmOpen}
 *     title="Delete Student Record"
 *     description="This will permanently remove John Baker's record and all associated data. This action cannot be undone."
 *     confirmLabel="Delete Record"
 *     variant="destructive"
 *     onConfirm={() => {
 *       deleteStudent(id)
 *       setIsConfirmOpen(false)
 *     }}
 *   />
 *
 *   // Non-destructive
 *   <ConfirmDialog
 *     open={isOpen}
 *     onOpenChange={setIsOpen}
 *     title="Unpublish Lunch Menu"
 *     description="The menu for next week will be hidden from student view."
 *     confirmLabel="Unpublish"
 *     onConfirm={handleUnpublish}
 *   />
 */

export interface ConfirmDialogProps {
	/** Whether the dialog is open */
	open: boolean
	/** Called when open state changes */
	onOpenChange: (open: boolean) => void
	/** Dialog title (Trajan Pro Bold) */
	title: string
	/** Dialog description (Garamond body text). Supports strings or ReactNode for rich formatting. */
	description: React.ReactNode
	/** Confirm button label */
	confirmLabel?: string
	/** Cancel button label */
	cancelLabel?: string
	/** Destructive variant (maroon confirm) or default (navy) */
	variant?: 'destructive' | 'default'
	/** Show warning icon */
	showIcon?: boolean
	/** Custom icon (replaces default warning) */
	icon?: React.ReactNode
	/** Loading state on confirm button */
	loading?: boolean
	/** Exact text that must be entered before confirmation is enabled */
	confirmationText?: string
	/** Label for the exact-text confirmation field */
	confirmationLabel?: string
	/** Placeholder for the exact-text confirmation field */
	confirmationPlaceholder?: string
	/** Current exact-text confirmation value */
	confirmationValue?: string
	/** Called when the exact-text confirmation value changes */
	onConfirmationValueChange?: (value: string) => void
	/** Confirm handler */
	onConfirm: () => void
	/** Additional class */
	className?: string
}

function WarningIcon() {
	return (
		<svg
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
			<path d="M12 9v4" />
			<path d="M12 17h.01" />
		</svg>
	)
}

export function ConfirmDialog({
	open,
	onOpenChange,
	title,
	description,
	confirmLabel = 'Confirm',
	cancelLabel = 'Cancel',
	variant = 'destructive',
	showIcon = true,
	icon,
	loading = false,
	confirmationText,
	confirmationLabel = 'Type to confirm',
	confirmationPlaceholder,
	confirmationValue = '',
	onConfirmationValueChange,
	onConfirm,
	className,
}: ConfirmDialogProps) {
	const cancelRef = React.useRef<HTMLButtonElement>(null)
	const contentRef = React.useRef<HTMLDivElement>(null)

	// Focus cancel button on open
	React.useEffect(() => {
		if (open) {
			const timer = setTimeout(() => cancelRef.current?.focus(), 50)
			return () => clearTimeout(timer)
		}
	}, [open])

	// Scroll lock
	React.useEffect(() => {
		if (open) {
			const prev = document.body.style.overflow
			document.body.style.overflow = 'hidden'
			return () => { document.body.style.overflow = prev }
		}
	}, [open])

	// Escape to close
	React.useEffect(() => {
		if (!open) return
		function handleKey(e: KeyboardEvent) {
			if (e.key === 'Escape') {
				e.preventDefault()
				onOpenChange(false)
			}
		}
		document.addEventListener('keydown', handleKey)
		return () => document.removeEventListener('keydown', handleKey)
	}, [open, onOpenChange])

	const confirmationMatches = confirmationText === undefined || confirmationValue === confirmationText

	// Enter to confirm
	React.useEffect(() => {
		if (!open || loading || !confirmationMatches) return
		function handleKey(e: KeyboardEvent) {
			if (e.key === 'Enter' && document.activeElement !== cancelRef.current) {
				e.preventDefault()
				onConfirm()
			}
		}
		document.addEventListener('keydown', handleKey)
		return () => document.removeEventListener('keydown', handleKey)
	}, [confirmationMatches, open, loading, onConfirm])

	if (!open) return null

	const iconColor = variant === 'destructive' ? 'text-error' : 'text-gold'
	const confirmClass = variant === 'destructive'
		? 'bg-error text-cream-50 hover:bg-error/90'
		: 'bg-navy text-cream-50 hover:bg-navy-600'

	return (
		<div
			className={cn('fixed inset-0 z-modal flex items-center justify-center p-4', className)}
		>
			{/* Backdrop */}
			<div
				className="absolute inset-0 bg-navy-950/50 backdrop-blur-sm animate-fade-in"
				onClick={() => onOpenChange(false)}
				aria-hidden="true"
			/>

			{/* Dialog */}
			<div
				ref={contentRef}
				role="alertdialog"
				aria-modal="true"
				aria-labelledby="confirm-title"
				aria-describedby="confirm-desc"
				className={cn(
					'relative z-10 w-full max-w-[420px]',
					'bg-card border border-border rounded-xl shadow-xl',
					'animate-scale-in',
				)}
			>
				{/* Content */}
				<div className="p-6">
					<div className="flex gap-4">
						{/* Icon */}
						{showIcon && (
							<div className={cn('flex-shrink-0 mt-0.5', iconColor)}>
								{icon || <WarningIcon />}
							</div>
						)}

						{/* Text */}
						<div className="flex-1 min-w-0">
							<h2
								id="confirm-title"
								className={cn(
									'font-display text-base uppercase tracking-trajan font-bold',
									'mb-2 text-foreground',
								)}
							>
								{title}
							</h2>
							<p
								id="confirm-desc"
								className="text-sm text-muted-foreground font-body leading-relaxed"
							>
								{description}
							</p>
						</div>
					</div>
					{confirmationText !== undefined && (
						<label className="mt-5 block font-ui text-sm font-semibold text-foreground">
							{confirmationLabel}
							<input
								type="text"
								value={confirmationValue}
								placeholder={confirmationPlaceholder ?? confirmationText}
								autoComplete="off"
								spellCheck={false}
								onChange={(event) => onConfirmationValueChange?.(event.target.value)}
								className="mt-2 h-11 w-full rounded-md border border-border bg-background px-3 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-gold/50"
							/>
						</label>
					)}
				</div>

				{/* Actions */}
				<div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/20 rounded-b-xl">
					<button
						ref={cancelRef}
						type="button"
						onClick={() => onOpenChange(false)}
						className={cn(
							'h-9 px-4 text-sm font-ui font-medium rounded-md',
							'bg-transparent border border-border text-foreground',
							'hover:bg-cream/60 transition-colors duration-fast',
						)}
					>
						{cancelLabel}
					</button>
					<button
						type="button"
						onClick={onConfirm}
						disabled={loading || !confirmationMatches}
						className={cn(
							'h-9 px-4 text-sm font-ui font-semibold rounded-md',
							confirmClass,
							'shadow-sm transition-all duration-fast',
							'disabled:opacity-50 disabled:cursor-not-allowed',
						)}
					>
						{loading ? (
							<span className="flex items-center gap-2">
								<svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
									<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
									<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
								</svg>
								Processing…
							</span>
						) : (
							confirmLabel
						)}
					</button>
				</div>
			</div>
		</div>
	)
}
